/**
 * Per-case filter store carrying the canonical `GraphView` for the active case.
 *
 * - `mode === 'chips'`: the `GraphView` is recomputed by the chip pattern-query
 *   pipeline whenever the chip set changes. The user adds/removes chips through
 *   the standard filter bar UI.
 * - `mode === 'custom'`: the `GraphView` was set externally (assistant handoff,
 *   saved-view rehydration). The chip set is preserved in `state.chips` so that
 *   `resetToChips()` returns to chip-driven rendering without losing the user's
 *   chip selection.
 *
 * View mutations (hide / unhide / setPosition / setLayoutHint) update the
 * current `GraphView`'s `view` slot directly. The mutated `GraphView` is what
 * the Save button serialises.
 *
 * Per-case isolation is enforced by `createFilterStore(caseId)` returning a
 * fresh store; `<FilterStoreProvider>` mounts one such store per case and
 * tears it down on unmount.
 */

import { createStore, type StoreApi } from 'zustand/vanilla'
import type { ActiveGraphFilter } from '@/types/graph-query'
import type { GraphView, GraphViewState, LayoutHint, Position } from '@/types/graph-view'
import { emptyViewState } from '@/types/graph-view'

/** Hide-rule kinds that the store understands. */
export type HideKind = 'id' | 'type' | 'relationshipType'

/**
 * Pluggable fetcher used by `setChipsAndFetch` and `resetToChips`.
 * The real implementation (frontend pattern-query union) is wired in by the
 * provider; tests inject mocks. Receives the current view state so that the
 * fetcher can carry positions/hides into the new `GraphView`.
 */
export type ChipFetcher = (args: {
  caseId: string
  chips: ActiveGraphFilter[]
  carry: GraphViewState
}) => Promise<GraphView>

const STORAGE_KEY_LEGACY_EXCLUSION = (caseId: string) => `graph-exclusion-${caseId}`
const STORAGE_KEY_MIGRATION_SENTINEL = (caseId: string) => `graph-view-migrated:${caseId}`

export interface FilterState {
  caseId: string
  graphView: GraphView | null
  chips: ActiveGraphFilter[]

  setChipsAndFetch: (chips: ActiveGraphFilter[]) => Promise<void>

  hide: (kind: HideKind, value: string) => void
  unhide: (kind: HideKind, value: string) => void
  setPosition: (nodeId: string, pos: Position) => void
  setLayoutHint: (hint: LayoutHint | undefined) => void
}

interface CreateOptions {
  caseId: string
  fetcher?: ChipFetcher
  /** Override `localStorage` for testing. */
  storage?: Pick<Storage, 'getItem' | 'setItem'>
}

const noopFetcher: ChipFetcher = async ({ caseId, carry }) => ({
  meta: {
    source: 'chips',
    caseId,
    createdAt: new Date().toISOString(),
    label: 'Filtered by 0 chips',
  },
  nodes: [],
  edges: [],
  view: carry,
})

/**
 * Read legacy `graph-exclusion-${caseId}` once, merge into `view.hidden*`, and
 * write a migration sentinel so subsequent loads do not re-apply.
 *
 * The legacy key is left in place for one release as a rollback safety net.
 */
function readLegacyExclusion(
  caseId: string,
  storage: Pick<Storage, 'getItem' | 'setItem'>
): Partial<GraphViewState> {
  try {
    if (storage.getItem(STORAGE_KEY_MIGRATION_SENTINEL(caseId)) === '1') {
      return {}
    }
    const raw = storage.getItem(STORAGE_KEY_LEGACY_EXCLUSION(caseId))
    if (!raw) {
      storage.setItem(STORAGE_KEY_MIGRATION_SENTINEL(caseId), '1')
      return {}
    }
    const parsed = JSON.parse(raw) as {
      hiddenEntityTypes?: string[]
      hiddenEntityIds?: string[]
      hiddenRelationshipTypes?: string[]
    }
    storage.setItem(STORAGE_KEY_MIGRATION_SENTINEL(caseId), '1')
    return {
      hiddenIds: parsed.hiddenEntityIds ?? [],
      hiddenTypes: parsed.hiddenEntityTypes ?? [],
      hiddenRelationshipTypes: parsed.hiddenRelationshipTypes ?? [],
    }
  } catch {
    return {}
  }
}

function ensureView(state: FilterState): GraphView {
  if (state.graphView) return state.graphView
  return {
    meta: {
      source: 'chips',
      caseId: state.caseId,
      createdAt: new Date().toISOString(),
    },
    nodes: [],
    edges: [],
    view: emptyViewState(),
  }
}

function uniquePush(arr: string[], value: string): string[] {
  return arr.includes(value) ? arr : [...arr, value]
}

/**
 * Module-level cache so the same per-case store is shared by every consumer,
 * even those outside the React `FilterStoreProvider` subtree (e.g. the agent
 * sidebar, mounted at `AppShell` level). Imperative callers use
 * `getOrCreateFilterStore({ caseId })`; in-tree React consumers go through
 * `useFilterStore` / `useFilterStoreApi`, which resolve the same instance.
 */
const STORE_CACHE = new Map<string, StoreApi<FilterState>>()

export function getOrCreateFilterStore(opts: CreateOptions): StoreApi<FilterState> {
  const existing = STORE_CACHE.get(opts.caseId)
  if (existing) return existing
  const created = createFilterStore(opts)
  STORE_CACHE.set(opts.caseId, created)
  return created
}

export function createFilterStore({
  caseId,
  fetcher = noopFetcher,
  storage = typeof window !== 'undefined' ? window.localStorage : undefined,
}: CreateOptions): StoreApi<FilterState> {
  const initialView = storage ? readLegacyExclusion(caseId, storage) : {}

  return createStore<FilterState>((set, get) => ({
    caseId,
    graphView: null,
    chips: [],

    async setChipsAndFetch(chips) {
      set({ chips })
      const carry: GraphViewState = {
        ...emptyViewState(),
        ...initialView,
        ...(get().graphView?.view ?? {}),
      }
      const view = await fetcher({ caseId, chips, carry })
      set({ graphView: view })
    },

    hide(kind, value) {
      const view = ensureView(get())
      const slot = (
        kind === 'id' ? 'hiddenIds' : kind === 'type' ? 'hiddenTypes' : 'hiddenRelationshipTypes'
      ) as keyof GraphViewState
      set({
        graphView: {
          ...view,
          view: {
            ...view.view,
            [slot]: uniquePush(view.view[slot] as string[], value),
          },
        },
      })
    },

    unhide(kind, value) {
      const current = get().graphView
      if (!current) return
      const slot = (
        kind === 'id' ? 'hiddenIds' : kind === 'type' ? 'hiddenTypes' : 'hiddenRelationshipTypes'
      ) as keyof GraphViewState
      set({
        graphView: {
          ...current,
          view: {
            ...current.view,
            [slot]: (current.view[slot] as string[]).filter(v => v !== value),
          },
        },
      })
    },

    setPosition(nodeId, pos) {
      const view = ensureView(get())
      set({
        graphView: {
          ...view,
          view: {
            ...view.view,
            positions: { ...view.view.positions, [nodeId]: pos },
          },
        },
      })
    },

    setLayoutHint(hint) {
      const view = ensureView(get())
      set({
        graphView: {
          ...view,
          view: { ...view.view, layoutHint: hint },
        },
      })
    },
  }))
}
